import { useState, useEffect } from "react";
import { 
  PoundSterling, 
  Plus, 
  CheckCircle, 
  Clock, 
  XCircle,
  ExternalLink,
  Copy,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentsPanel = ({ token }) => {
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ total_received: 0, total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [leads, setLeads] = useState([]);
  
  const [newPayment, setNewPayment] = useState({
    quote_id: "",
    amount: "",
    payment_type: "deposit",
    notes: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPayments();
    fetchLeads();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/all`, { headers });
      setPayments(response.data.payments || []);
      setTotals(response.data.totals || { total_received: 0, total_pending: 0 });
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/owner/leads`, { headers });
      setLeads(response.data || []);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  const handleCreatePayment = async () => {
    if (!newPayment.quote_id || !newPayment.amount) {
      toast.error("Please select a job and enter an amount");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/payments/create`, {
        quote_id: newPayment.quote_id,
        amount: parseFloat(newPayment.amount),
        payment_type: newPayment.payment_type,
        notes: newPayment.notes
      }, { headers });

      toast.success("Bank-transfer payment request created");
      if (response.data.payment_reference) {
        navigator.clipboard.writeText(response.data.payment_reference);
        toast.info(`Reference ${response.data.payment_reference} copied`);
      }

      setShowCreateForm(false);
      setNewPayment({ quote_id: "", amount: "", payment_type: "deposit", notes: "" });
      fetchPayments();
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setCreating(false);
    }
  };

  const markAsPaid = async (paymentId) => {
    try {
      await axios.put(`${API}/payments/${paymentId}/status?status=paid`, {}, { headers });
      toast.success("Payment marked as paid");
      fetchPayments();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const copyPaymentLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Payment link copied!");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Paid
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <XCircle className="w-3 h-3" />
            {status}
          </span>
        );
    }
  };

  const getPaymentTypeLabel = (type) => {
    const labels = {
      deposit: "Deposit",
      stage_payment: "Stage Payment",
      variation_payment: "Variation",
      final_payment: "Final Payment"
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payments</h2>
          <p className="text-gray-600">Create and track payment requests</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />
          Create Bank-Transfer Request
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <PoundSterling className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Received</p>
              <p className="text-2xl font-bold text-gray-900">£{totals.total_received.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending Payments</p>
              <p className="text-2xl font-bold text-gray-900">£{totals.total_pending.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{payments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Payment Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Create Payment Request</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Select Job/Quote</Label>
              <select
                value={newPayment.quote_id}
                onChange={(e) => setNewPayment(prev => ({ ...prev, quote_id: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select a job...</option>
                {leads.map(lead => (
                  <option key={lead.id} value={lead.id}>
                    {lead.customer_name || lead.name} - {lead.project_type || "Project"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Amount (£)</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="500.00"
                value={newPayment.amount}
                onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label>Payment Type</Label>
              <select
                value={newPayment.payment_type}
                onChange={(e) => setNewPayment(prev => ({ ...prev, payment_type: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="deposit">Deposit</option>
                <option value="stage_payment">Stage Payment</option>
                <option value="variation_payment">Variation Payment</option>
                <option value="final_payment">Final Payment</option>
              </select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="e.g., 25% deposit for bathroom project"
                value={newPayment.notes}
                onChange={(e) => setNewPayment(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <Button
              onClick={handleCreatePayment}
              disabled={creating}
              className="bg-primary hover:bg-primary/90"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <PoundSterling className="w-4 h-4 mr-2" />
                  Create Bank-Transfer Request
                </>
              )}
            </Button>
            <Button variant="outline" onClick={() => setShowCreateForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Payments List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold">Recent Payments</h3>
        </div>
        
        {payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <PoundSterling className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No payments yet</p>
            <p className="text-sm">Create your first payment request above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map(payment => (
                  <tr key={payment.payment_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{payment.customer_name || "Customer"}</p>
                      <p className="text-sm text-gray-500">{payment.customer_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">{getPaymentTypeLabel(payment.payment_type)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-gray-900">£{payment.amount?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(payment.payment_status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {payment.payment_reference && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyPaymentLink(payment.payment_reference)}
                            title="Copy payment reference"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        )}
                        {payment.payment_status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsPaid(payment.payment_id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Mark as paid"
                          >
                            <CheckCircle className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsPanel;
